import React, { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Grid } from "@mui/material";
import { t } from "i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoryListService from "../service/categoryService";
import ProductAccordian from "./ProductAccordian";
import FailedTransactionItem from "./FailedTransactionItem";
import FailedInput from "./FailedInput";
import { CategoryFromOrTo, CategoryTransaction, FailedTransaction, ProductCategoryMappingRequest, SuccessTransaction, TransactionCardProps } from "../../../interfaces/CategoryTransactionInterface";
import SuccessInput from "./SuccessInput";
import TransactionRecordItem from "./TransactionRecordItem";
import SuccessTransactionItem from "./SuccessTransactionItem";


const TransactionCard = <T extends boolean>(props: TransactionCardProps<T>) => {

    const { isSuccess, transactionList, isFind, showSnackbar, handleLoader, apiCall, limit } = props;

    const [expanded, setExpanded] = useState<any>(false);
    const [activeAccordionIndex, setActiveAccordionIndex] = useState<number | null>(null);
    const [accordionPayloadData, setAccordionPayloadData] = useState<{ [key: number]: ProductCategoryMappingRequest[] }>({});
    const [accordionButtonStates, setAccordionButtonStates] = useState<{ [key: number]: boolean }>({});

    const handleAccordion = (panel: string, index: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
        setActiveAccordionIndex(isExpanded ? index : null);
    };

    const shouldDisableUpdateButton = (payloadData: ProductCategoryMappingRequest[]): boolean => {
        if (!payloadData || payloadData.length === 0) return true;

        // Function to check if a row is completely filled (both fields have valid values)
        const isRowFilled = (row: ProductCategoryMappingRequest) => {
            return row?.categoryCode !== undefined && row?.categoryCode?.trim() !== "" && row?.publishStatus !== undefined;
        };

        // Function to check if a row is partially filled (one field is missing)
        const isRowHalfFilled = (row: ProductCategoryMappingRequest) => {
            return (row?.publishStatus === undefined && (row?.categoryCode && row?.categoryCode?.trim() !== "")) ||
                ((row?.categoryCode === undefined || row?.categoryCode?.trim() === "") && row?.publishStatus !== undefined);
        };

        // If there are two rows, check conditions:
        const isFirstRowFilled = isRowFilled(payloadData[0]);
        const isSecondRowFilled = isRowFilled(payloadData[1]);
        const isFirstRowHalfFilled = isRowHalfFilled(payloadData[0]);
        const isSecondRowHalfFilled = isRowHalfFilled(payloadData[1]);

        if ((isFirstRowFilled || isSecondRowFilled) && !(isFirstRowHalfFilled || isSecondRowHalfFilled)) {
            return false;
        }

        if ((isFirstRowHalfFilled || isSecondRowHalfFilled)) {
            return true;
        }

        return true; // Disable button
    };

    /**
    * Determines if any old categories differ from new categories in the failed transaction list.
    * @param failedTransactionList - List of failed transactions.
    * @returns True if any oldCategory differs from newCategory, otherwise false.
    */
    const inputFieldDecider = (failedTransactionList: FailedTransaction[],productItem : CategoryTransaction): boolean => {
        for (const item of failedTransactionList) {
            if (item.oldCategory && item.oldCategory !== item.newCategory) {
                return true;
            }
            if((productItem?.publicFlag === "0" && item.categoryTo?.publicFlag ===1) || ( item.categoryFrom?.publicFlag===1 && item.categoryTo.publicFlag === 0 ))
                return false;
        }
        return false;
    };
    useEffect(() => {
        if (activeAccordionIndex !== null) {
            const currentPayload = accordionPayloadData[activeAccordionIndex] || [];
            setAccordionButtonStates(prev => ({
                ...prev,
                [activeAccordionIndex]: shouldDisableUpdateButton(currentPayload)
            }));
        }
    }, [activeAccordionIndex, accordionPayloadData]);

    // Effect hook to update payloadData when we get the prefilled fields
    useEffect(() => {
        if (!isSuccess && Array.isArray(transactionList)) {
            transactionList.forEach((item, index) => {
                if ("failedCategory" in item && item.failedCategory.length > 0) {
                    const { fromPreviousValue, toPreviousValue } = extractPreviousFailedValues(item.failedCategory);
                    const shouldHaveTwoObjects = inputFieldDecider(item.failedCategory,item);
                    const forOneRowFields = [{
                        categoryCode: toPreviousValue?.categoryCode || "",
                        publishStatus: toPreviousValue?.publishStatus,
                    }];

                    const forTwoRowsFields = [
                        {
                            categoryCode: fromPreviousValue?.categoryCode || "",
                            publishStatus: fromPreviousValue?.publishStatus,
                        },
                        {
                            categoryCode: toPreviousValue?.categoryCode || "",
                            publishStatus: toPreviousValue?.publishStatus,
                        }
                    ];

                    setAccordionPayloadData(prev => ({
                        ...prev,
                        [index] : shouldHaveTwoObjects ? forTwoRowsFields : forOneRowFields .filter(Boolean) as ProductCategoryMappingRequest[]
                    }));
                }
                else {
                    setAccordionPayloadData(prev => ({
                        ...prev,
                        [index]: [
                            {
                                categoryCode: item.productCategory,
                                publishStatus: 9,
                            },
                        ].filter(Boolean) as ProductCategoryMappingRequest[]
                    }));
                }
            });
        }
    }, [transactionList, isSuccess]);

    /**
    * Returns the flag status based on the provided publicStatus value.
    * 
    * @param {number} publicStatus - The public status code.
    * @returns {string} - The corresponding status as a string.
    */
    const getFlagStatus = (publicStatus: string) => {
        switch (publicStatus) {
        case "0":
            return t("category_management.private_flag_status");
        case "1":
            return t("category_management.public_flag_status");
        case "9":
            return t("category_management.archive_flag_status");
        }
    };

    /**
        * @description to show the status value text according to status code.
        * @param requestStatusCode - request status code.
        * @returns string - text for each status code.
        */

    const getRequestStatus = (requestStatusCode: number) => {
        switch (requestStatusCode) {
        case 0:
            return t("category_management.pending_request_status");
        case 1:
            return t("category_management.pending_request_status");
        case 2:
            return t("category_management.success_request_status");
        case 3:
            return t("category_management.failed_request_status");
        case 4:
            return t("category_management.end_request_status");
        }
    };

    const refractorPayload = (currentPayload: any[], requiredKeys: (keyof ProductCategoryMappingRequest)[]) => {
        return currentPayload.map(item => {
            const cleanedItem = { ...item };
            for (const key of requiredKeys) {
                if (typeof cleanedItem[key] === "string") {
                    cleanedItem[key] = cleanedItem[key].trim();
                }
            }
            return cleanedItem;
        })
            .filter(item =>
                requiredKeys.every(key => {
                    const value = item[key];
                    return value !== undefined && value !== null && value !== "";
                })
            );
    };


    /**
     * 
     * @param cainzProductCode - The Cainz product code.
     * @returns void
     * @description Calls the productCategoryMapping Api to update the product category mapping.
     */

    const productCategoryMapping = (cainzProductCode: string, index: number) => {
        handleLoader(true);
        const currentPayload = accordionPayloadData[index] || [];
        new CategoryListService(refractorPayload(currentPayload,["categoryCode", "publishStatus"])).productCategoryMapping(cainzProductCode)
            .then((res) => {
                showSnackbar(res.message, true);
                apiCall(1, limit);
                handleLoader(false);
            })
            .catch((err) => {
                showSnackbar(err?.message, false);
                handleLoader(false);
            });
        
    };

    /**
    * Extracts previous category values from a failed transaction list.
    * @param failedCategory - List of failed category transactions.
    * @returns Extracted previous values for comparison.
    */
    const extractPreviousFailedValues = (failedCategory : FailedTransaction[]) => {
        if (!failedCategory || failedCategory.length === 0) {
            return { fromPreviousValue: null, toPreviousValue: null };
        }
        const firstTransaction = failedCategory[0];
        const lastTransaction = failedCategory[failedCategory.length - 1];

        let fromPreviousValue : {
            categoryCode: string;
            publishStatus: number;
        };

        if (firstTransaction?.categoryFrom?.categoryCode && firstTransaction.categoryFrom?.publicFlag){
            fromPreviousValue= {
                categoryCode: (firstTransaction?.categoryFrom ?? firstTransaction?.categoryTo )?.categoryCode,
                publishStatus: (firstTransaction?.categoryFrom ?? firstTransaction?.categoryTo )?.publicFlag
            };    
        }else{
            fromPreviousValue= {
                categoryCode: firstTransaction?.categoryTo?.categoryCode,
                publishStatus: firstTransaction.categoryTo?.publicFlag
            };
        }

        return {
            fromPreviousValue: fromPreviousValue,
            toPreviousValue: {
                categoryCode: (lastTransaction?.categoryTo ??lastTransaction?.categoryFrom )?.categoryCode,
                publishStatus: (lastTransaction?.categoryTo ??lastTransaction?.categoryFrom )?.publicFlag 
            }
        };
    };

    const transactionItemToSuccessItemConverter = (data : CategoryTransaction) :  SuccessTransaction => {
        return {
            categoryFrom: {} as CategoryFromOrTo,
            categoryTo: {
                categoryCode: data.productCategory,
                publicFlag: parseInt(data.publicFlag),
                requestStatus: 0,
                updatedAt: "",
                responseCode: 0
            }
        };
    };


    const showSuccessInputs = (transactionItem : CategoryTransaction ) => {
        if(isFind){
            // no data - shown
            if(transactionItem.successCategory.length === 0 && transactionItem.adminActions.length === 0 && transactionItem.failedCategory?.length === 0)
                return true;
            
            // failed and success data by seller - hided
            if(transactionItem.successCategory.length > 0 && transactionItem.failedCategory.length > 0 && transactionItem.adminActions.length === 0)
                return false;

            // success data by seller - shown
            if(transactionItem.successCategory.length > 0 && transactionItem.failedCategory?.length === 0 && transactionItem?.adminActions?.length === 0)
                return true;

            // success data by seller and admin - shown
            if(transactionItem.successCategory.length > 0 && transactionItem.failedCategory?.length === 0 && transactionItem?.adminActions?.length > 0)
                return true;

            // failed data by seller - hided
            if( transactionItem?.successCategory?.length === 0 && transactionItem?.failedCategory?.length > 0 && transactionItem?.adminActions?.length === 0 ){
                return false;
            }

            // success data by admin - shown
            if(transactionItem?.successCategory?.length === 0 && transactionItem?.failedCategory?.length === 0 && transactionItem?.adminActions?.length > 0){
                return true;
            }
        }
    };

    const showSuccessAccordian = (transactionItem : CategoryTransaction) => {

        // no data
        if(transactionItem?.successCategory?.length === 0 && transactionItem?.failedCategory?.length ===0 && transactionItem?.adminActions?.length === 0)
            return true;

        // success and failed data of seller
        if(transactionItem?.successCategory?.length > 0 && transactionItem?.failedCategory?.length > 0 && transactionItem?.adminActions?.length === 0)
            return true;

        // success data of seller
        if(transactionItem?.successCategory?.length > 0  && transactionItem?.adminActions?.length === 0 )
            return true;

        // success data of admin
        if(transactionItem?.successCategory?.length === 0  && transactionItem?.adminActions?.length > 0 )
            return true;

        // success data of seller and admin 
        if(transactionItem?.successCategory?.length > 0 && transactionItem?.adminActions?.length > 0 )
            return true;
        return false;
    };

    return (
        <Grid mt={3}>
            {
                transactionList?.map((transactionItem: CategoryTransaction, index) =>
                    (
                        <Accordion key={index} expanded={expanded === `panel${index}`} onChange={handleAccordion(`panel${index}`, index)}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                                sx={{ userSelect: "text" }}
                            >
                                <ProductAccordian item={transactionItem} getFlagStatus={getFlagStatus} />
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Success Accoridian Starts here */}
                                <Accordion sx={{boxShadow:"none"}}>
                                    {
                                        showSuccessAccordian(transactionItem) && 
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}
                                                aria-controls={`panel${index}-content`}
                                                id={`panel${index}-header`}
                                                sx={{backgroundColor:"#f4f4f4"}}         
                                            >
                                                <SuccessTransactionItem 
                                                    successItem={transactionItem?.successCategory[0]?? transactionItemToSuccessItemConverter(transactionItem)} 
                                                    getFlagStatus={getFlagStatus} 
                                                    transactionItem={transactionItem}/>
                                            </AccordionSummary>
                                    }
                                    <AccordionDetails sx={isSuccess ? {margin:-2} : null}  >
                                        { 
                                            transactionItem?.successCategory?.length > 0 && transactionItem.successCategory[0] && 
                                        <Grid py={1} px={3} mt={-1} mx={isFind?-2:0} sx={{backgroundColor:"#f4f4f4", marginLeft: !isSuccess? -2 : 0, marginRight:!isSuccess?-2 : 0}}>
                                            {
                                                transactionItem.successCategory[0]?.categoryFrom?.categoryCode !==undefined && 
                                                <TransactionRecordItem 
                                                    transactionRecord={transactionItem.successCategory[0]?.categoryFrom} 
                                                    getFlagStatus={getFlagStatus} 
                                                    getRequestStatus={getRequestStatus} />
                                            }
                                            {
                                                transactionItem.successCategory[0]?.categoryTo?.categoryCode !== undefined &&
                                                <TransactionRecordItem
                                                    transactionRecord={transactionItem.successCategory[0]?.categoryTo}
                                                    getFlagStatus={getFlagStatus} 
                                                    getRequestStatus={getRequestStatus} />
                                            }
                                        </Grid>
                                        }
                                        {
                                            transactionItem?.adminActions?.length > 0 &&
                                                    <Grid mt={transactionItem.successCategory[0]?1:2}>
                                                        {
                                                            transactionItem?.adminActions?.map((actionItem: SuccessTransaction, actionIdx: number) => {
                                                                return (
                                                                    <Grid key={actionIdx} mt={1} py={1} px={3} mx={isFind?-2:0} sx={{ backgroundColor: "#dafff5" }} >
                                                                        {
                                                                            actionItem.categoryFrom?.categoryCode !== undefined && 
                                                                            <TransactionRecordItem 
                                                                                transactionRecord={actionItem.categoryFrom}
                                                                                getFlagStatus={getFlagStatus}
                                                                                getRequestStatus={getRequestStatus}  />
                                                                        }
                                                                        {
                                                                            actionItem.categoryTo?.categoryCode !==undefined && 
                                                                            <TransactionRecordItem
                                                                                transactionRecord={actionItem.categoryTo}
                                                                                getFlagStatus={getFlagStatus}
                                                                                getRequestStatus={getRequestStatus} />
                                                                        }
                                                                    </Grid>
                                                                );
                                                            })
                                                        }
                                                    </Grid>
                                        }
                                        {
                                            showSuccessInputs (transactionItem) && activeAccordionIndex!==null && <SuccessInput
                                                transactionItem={transactionItem}
                                                productCategoryMapping={productCategoryMapping}
                                                accordionPayloadData={accordionPayloadData}
                                                setAccordionPayloadData={setAccordionPayloadData}
                                                activeAccordionIndex={activeAccordionIndex}
                                                accordionButtonStates={accordionButtonStates}
                                                index={index} 
                                                isFind={isFind}
                                            />
                                        }
                                    </AccordionDetails>
                                </Accordion>
                                {/* Failed Accordian Starts here */}
                                {
                                    (isFind || !isSuccess) && transactionItem?.failedCategory && transactionItem?.failedCategory?.map((failedItem: FailedTransaction, failedIdx: number) => {
                                        return (
                                            <Accordion key={failedIdx} sx={{boxShadow:"none" , backgroundColor:"#f4f4f4"}} >
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />}
                                                    aria-controls={`panel${index}-content`}
                                                    id={`panel${index}-header`}
                                                    
                                                >
                                                    <FailedTransactionItem failedTransaction={failedItem} getFlagStatus={getFlagStatus} transactionItem={transactionItem} />
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {
                                                        failedItem.categoryFrom?.categoryCode !== undefined &&
                                                    <TransactionRecordItem transactionRecord={failedItem.categoryFrom} getFlagStatus={getFlagStatus} getRequestStatus={getRequestStatus} />
                                                    }
                                                    {
                                                        failedItem.categoryTo?.categoryCode!== undefined &&
                                                    <TransactionRecordItem transactionRecord={failedItem.categoryTo} getFlagStatus={getFlagStatus} getRequestStatus={getRequestStatus} />
                                                    }
                                                </AccordionDetails>
                                                {
                                                    transactionItem?.failedCategory?.length === failedIdx + 1 && transactionItem?.failedCategory?.length > 0 && activeAccordionIndex !== null &&
                                                    <FailedInput
                                                        transactionItem={transactionItem}
                                                        productCategoryMapping={productCategoryMapping}
                                                        accordionPayloadData={accordionPayloadData}
                                                        setAccordionPayloadData={setAccordionPayloadData}
                                                        activeAccordionIndex={activeAccordionIndex}
                                                        accordionButtonStates={accordionButtonStates}
                                                        index={index}
                                                        inputFieldDecider={inputFieldDecider} />
                                                }
                                            </Accordion>
                                        );
                                    })
                                }
                            </AccordionDetails>
                        </Accordion>
                    )
                )
            }
        </Grid>
    );
};

export default TransactionCard;
